<template>
  <div class="multi-step-quiz-v3">
    <div class="multi-step-quiz-v3-section spacing-bottom--24">
      <h2 class="section-title spacing-bottom--12">{{ $t("shortcodes.multiStepQuiz.appearance") }}</h2>
      <div class="appearance-input-wrapper">
        <VaMediaObjectFileInput
          :label="$t('shortcodes.multiStepQuiz.agent_avatar')"
          class="quiz-image-input"
          :item-id="quizData.imageID"
          @update:photo="updateAvatar($event)" />
        <VaColorPicker
          class="color-picker-input"
          position="bottom"
          :title="$t('resources.domains.primary_colour')"
          v-model="quizData.primaryColor"></VaColorPicker>
      </div>
    </div>
    <hr class="quiz-hr" />
    <div class="multi-step-quiz-v3-section spacing-bottom--24">
      <h2 class="section-title spacing-bottom--4">{{ $t("shortcodes.multiStepQuiz.headline") }}</h2>
      <div class="section-subtitle">{{ $t("shortcodes.multiStepQuiz.headline_subtitle") }}</div>
      <va-tinymce-input
        class="w-full include-border"
        :current-content="quizData.headline.content"
        :editor-height="300"
        stripped-down
        @update="content => updateHeadlineContent(quizData.headline, content)" />
    </div>
    <hr class="quiz-hr" />
    <div class="multi-step-quiz-v3-section">
      <h2 class="section-title spacing-bottom--12">{{ $t("shortcodes.multiStepQuiz.quiz") }}</h2>
      <!--- Questions list -->
      <template v-for="(question, questionIndex) in quizData.questions">
        <div class="quiz-question" :key="`question-${questionIndex}`">
          <div class="quiz-item">
            <div class="quiz-item__header">
              <span class="quiz-item__header-title">Question {{ questionIndex + 1 }}</span>
              <span class="quiz-item__remove ml-auto" @click="removeQuestion(questionIndex)">
                {{ $t("shortcodes.multiStepQuiz.delete") }}
              </span>
            </div>
            <va-tinymce-input
              class="w-full"
              :current-content="question.content"
              :editor-height="300"
              stripped-down
              @update="content => updateQuestionContent(question, content)" />
          </div>
          <!--- Answers list -->
          <div class="quiz-question__answers-wrapper">
            <template v-for="(answer, answerIndex) in question.answers">
              <div
                :class="[
                  successDestination(answer) ? 'quiz-question__answer--success' : '',
                  failDestination(answer) ? 'quiz-question__answer--fail' : '',
                ]"
                class="quiz-question__answer d-flex align-center"
                :key="`answer-${answerIndex}`">
                <v-text-field
                  v-model="quizData.questions[questionIndex].answers[answerIndex].content"
                  :label="`Answer ${answerIndex + 1}`"></v-text-field>

                <v-tooltip top>
                  <template #activator="{ on }">
                    <v-icon
                      @click="toggleSuccessAnswer(answer)"
                      v-on="on"
                      class="quiz-question__answers-icon"
                      color="icon"
                      :class="[successDestination(answer) ? 'quiz-question__answers-icon-active--success' : '']">
                      mdi-check</v-icon
                    >
                  </template>
                  <p>Add destination - Success</p>
                </v-tooltip>

                <v-tooltip top>
                  <template #activator="{ on }">
                    <v-icon
                      @click="toggleFailAnswer(answer)"
                      v-on="on"
                      class="quiz-question__answers-icon"
                      color="icon"
                      :class="[failDestination(answer) ? 'quiz-question__answers-icon-active--fail' : '']">
                      mdi-close</v-icon
                    >
                  </template>
                  <p>Add destination - Fail</p>
                </v-tooltip>

                <v-tooltip top>
                  <template #activator="{ on }">
                    <v-icon
                      @click="removeAnswer(question, answerIndex)"
                      v-on="on"
                      class="quiz-question__answers-icon--delete ml-2"
                      color="icon">
                      mdi-delete-forever</v-icon
                    >
                  </template>
                  <p>Remove answer</p>
                </v-tooltip>
              </div>
            </template>
            <button class="multi-step-quiz-v3-section__button-answer" @click="addAnswer(question)">
              <plus-icon class="fill--primary mr-2"></plus-icon> Add Answer
            </button>
          </div>
        </div>
      </template>
      <!--- Add Question btn -->
      <button class="multi-step-quiz-v3-section__button-question" :disabled="addQuestionDisabled" @click="addQuestion">
        <plus-icon class="fill--primary mr-2"></plus-icon>
        Add Question
      </button>
      <!---Destinations-->
      <div class="destinations__wrapper">
        <div class="quiz-item quiz-item--success">
          <div class="quiz-item__header">
            <span class="quiz-item__header-title">Success Destination </span>
          </div>
          <va-tinymce-input
            class="w-full"
            :current-content="quizData.destinations.success.content"
            :editor-height="300"
            stripped-down
            @update="content => updateDestinationContent(quizData.destinations.success, content)" />
        </div>

        <div class="quiz-item quiz-item--fail">
          <div class="quiz-item__header">
            <span class="quiz-item__header-title">Fail Destination</span>
          </div>
          <va-tinymce-input
            class="w-full"
            :current-content="quizData.destinations.fail.content"
            :editor-height="300"
            stripped-down
            @update="content => updateDestinationContent(quizData.destinations.fail, content)" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import cloneDeep from "lodash/cloneDeep";
  import PlusIcon from "@/components/icons/PlusIcon";
  import VaColorPicker from "@/components/custom/VaColorPicker";
  import { serializeDynamicComponents } from "@/assets/utilities/tinymce/visualization";

  const DEFAULT_QUESTION = {
    content: "Placeholder question",
    answers: [
      {
        content: "Yes",
        destination: null,
      },
      {
        content: "No",
        destination: null,
      },
    ],
  };
  export default {
    components: {
      PlusIcon,
      VaColorPicker,
    },
    data() {
      return {
        quizData: {
          imageID: null,
          imageURL: "",
          headline: {
            content: "",
          },
          primaryColor: "#0D6EFD",
          questions: [
            {
              content: "Placeholder question",
              answers: [
                {
                  content: "Yes",
                },
                {
                  content: "No",
                },
              ],
            },
          ],
          destinations: {
            success: {
              content: "",
            },
            fail: {
              content: "",
            },
          },
        },
      };
    },
    beforeMount() {
      // Mounts a postmessage listener to window. Waits for "sendData" message from /multiStepQuizV2Config.
      this.setupReceiveDataListener();
      // As soon as receiveDataListener is ready, we can request data from /multiStepQuizV2Config.
      this.requestData();

      return;
    },
    computed: {
      addQuestionDisabled() {
        return !!find(this.quizData.questions, question => {
          return question.answers.every(answer => answer.destination);
        });
      },
      emitData() {
        return {
          quizData: this.quizData,
        };
      },
    },
    watch: {
      quizData: {
        deep: true,
        handler() {
          this.updateValue(this.emitData);
        },
      },
    },
    methods: {
      successDestination(answer) {
        return answer.destination === "success";
      },
      failDestination(answer) {
        return answer.destination === "fail";
      },
      updateAvatar(value) {
        if (value) {
          this.quizData.imageID = value["@id"];
          this.quizData.imageURL = value.contentUrl;
        } else {
          this.quizData.imageID = "";
          this.quizData.imageURL = "";
        }
      },
      removeQuestion(questionIndex) {
        this.quizData.questions.splice(questionIndex, 1);
      },
      updateQuestionContent(question, content) {
        question.content = content;
      },
      updateDestinationContent(destination, content) {
        destination.content = content;
      },
      removeAnswer(question, answerIndex) {
        question.answers.splice(answerIndex, 1);
      },
      updateHeadlineContent(destination, content) {
        destination.content = content;
      },
      addAnswer(question) {
        question.answers.push({
          content: "",
        });
      },
      toggleSuccessAnswer(answer) {
        if (answer.destination === "success") {
          this.$set(answer, "destination", null);
        } else {
          this.$set(answer, "destination", "success");
        }
      },
      toggleFailAnswer(answer) {
        if (answer.destination === "fail") {
          this.$set(answer, "destination", null);
        } else {
          this.$set(answer, "destination", "fail");
        }
      },
      addQuestion() {
        // CloneDeep to discard the reference between "answers" inner object of Question object.
        // Without this, editing a specific answer would edit all the answers (because reference would be kept).
        const newQuestion = cloneDeep(DEFAULT_QUESTION);
        this.quizData.questions.push(newQuestion);
      },
      setupReceiveDataListener() {
        window.onmessage = message => {
          if (message.data.type === "sendData") {
            // Format and set the received data
            const { data } = message.data;
            if (data !== "undefined") {
              const jsonData = JSON.parse(data, (key, value) => {
                if (key === "content") {
                  return decodeURIComponent(atob(value));
                }

                return value;
              });
              this.quizData = jsonData?.quizData || this.quizData;
            }
          }
        };
      },
      requestData() {
        window.parent.postMessage(
          {
            mceAction: "requestData",
          },
          origin,
        );
        this.updateValue(this.emitData);
      },
      updateValue(val) {
        const stringifiedData = JSON.stringify(val, (key, value) => {
          if (key === "content") {
            // Replacing &emsp; with space, cause innerHTML doesn't deserialize from space to $emsp;
            value = value.replace(/&emsp;/g, " ");
            let parser = new DOMParser();
            // Creating document from string to run dynamic component serialization which uses DOM manipulation
            let document = parser.parseFromString(value, "text/html");
            serializeDynamicComponents(document);
            // Selecting only the body content of the document as the wrappers are not needed
            document = document.querySelector("body").innerHTML;

            return window.btoa(encodeURIComponent(document));
          }

          return value;
        });

        window.parent.postMessage(
          {
            mceAction: "updateData",
            data: stringifiedData,
          },
          origin,
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../@core/preset/preset/typography/index.scss";
  @import "../@core/preset/preset/functions.scss";

  .multi-step-quiz-v3 {
    margin-top: -12px;
    margin-left: -12px;
    height: calc(100% + 32px);
    width: calc(100% + 24px);
    background: white;
  }

  .multi-step-quiz-v3-section {
    padding-top: 24px;
    padding-left: 24px;
    padding-right: 24px;

    .section-title {
      @include text-body-1-bold();
      color: get-color("body");
    }

    .section-subtitle {
      @include text-subtitle-2();
      color: get-color("subtitle");
      margin-bottom: 12px;
    }

    &__button {
      &-question {
        width: 100%;
        text-align: center;
        background-color: get-color("primary-10-hover");
        border: 1px dashed get-color("primary-65");
        border-radius: 6px;
        padding: 8px;
        @include text-button-medium();
        color: get-color("primary");
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &-answer {
        margin-left: auto;
        margin-top: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        @include text-button-medium();
        color: get-color("primary");
      }
    }
  }

  .quiz-question {
    padding: 12px;
    border: 1px dashed get-color("primary");
    background: get-color("primary-10-hover");
    margin-bottom: 24px;
    // Answers
    &__answers {
      &-title {
        color: get-color("subtitle");
        @include heading-4();
        margin: 7px 0px;
      }
      &-wrapper {
        display: flex;
        flex-direction: column;
        padding: 0 10px 0 20px;
      }
      &-icon {
        height: 24px;
        width: 24px;
        border-radius: 50%;
        margin: 0 8px;
        cursor: pointer;
        transition: none !important;
        &::after {
          content: none !important;
        }
        &--delete {
          &:hover {
            color: get-color("error") !important;
          }
        }
        &-active {
          &--success {
            &.icon--text {
              color: get-color("success") !important;
            }
          }
          &--fail {
            &.icon--text {
              color: get-color("error") !important;
            }
          }
        }
      }
    }
    &__answer {
      position: relative;
      padding: 5px 10px;
      margin: 10px 0;
      border: 1px dashed get-color("primary");
      border-radius: 5px;
      background: get-color("element-bg-100");
      &--success {
        border: 2px dashed get-color("success");
      }
      &--fail {
        border: 2px dashed get-color("error");
      }
      &::before {
        content: " ";
        position: absolute;
        height: 110px;
        left: -10px;
        top: 0;
        border: 1px dashed get-color("primary");
        border-left: none;
      }
      &::after {
        content: " ";
        position: absolute;
        height: 1px;
        width: 10px;
        left: -10px;
        top: 50%;
        transform: translateY(-50%);
        border: 1px dashed get-color("primary");
        border-bottom: none;
        border-left: none;
        border-top-right-radius: 10px;
      }
      &:first-of-type {
        &::before {
          top: -10px;
          height: 120px;
        }
      }
      &:last-of-type {
        &::before {
          top: -10px;
          height: 55px;
          border-bottom-right-radius: 10px;
        }
      }
    }
  }

  .quiz-item {
    border: 1px dashed get-color("primary");
    border-radius: 5px;
    padding: 2px;

    &--success {
      background-color: get-color("primary-10-hover");
      border: 1px dashed get-color("success");
      .va-code-input {
        margin-top: 10px;
      }
    }

    &--fail {
      background-color: get-color("primary-10-hover");
      border: 1px dashed get-color("error");
    }

    &__header {
      display: flex;
      padding: 5px;
      border-top-left-radius: 5px;
      border-top-right-radius: 5px;
      &-title {
        color: get-color("subtitle");
        @include text-caption-bold();
      }
    }

    &__remove {
      cursor: pointer;
      color: get-color("error");
      @include text-caption();
      &::after {
        content: none !important;
      }
    }

    .display-reset-checkbox {
      margin-top: 16px;
      &::v-deep {
        label {
          color: get-color("subtitle");
          @include text-body-1-bold();
        }
      }
    }
  }

  .destinations__wrapper {
    .quiz-item {
      margin: 10px 0;
    }
  }

  .include-border {
    ::v-deep {
      .tox-tinymce {
        border: 1px solid get-color("border") !important;
      }
    }
  }

  ::v-deep {
    .tox-tinymce {
      border: none !important;
    }
    .va-tinymce-input .va-tinymce-input__label-wrapper {
      margin-bottom: 0;
    }
  }

  .quiz-error {
    @include text-caption();
    color: get-color("error");
    margin-top: 5px;
  }

  .quiz-hr {
    border: 1px solid get-color("border");
    position: relative;
    border-top: 0;
    margin: 0 -12px;
  }

  .quiz-image-input {
    width: 100%;

    ::v-deep {
      .image-input-block {
        background: get-color("same-white");
        padding-top: 9px;
        padding-bottom: 9px;

        button {
          background-color: get-color("same-white") !important;
          color: get-color("primary") !important;
          border: 1px solid get-color("primary") !important;
          cursor: pointer;

          input {
            cursor: pointer;
          }

          input[type="file"]::-webkit-file-upload-button {
            cursor: pointer;
          }

          &:hover {
            background-color: get-color("same-white") !important;
            color: get-color("primary") !important;
          }
        }
      }

      .image-input-label.text--primary {
        @include text-caption-bold();
        line-height: 14px !important;
        color: get-color("subtitle") !important;
        padding-bottom: 6px !important;
      }

      .fileType__error {
        @include text-caption();
        color: get-color("error");
        margin: 0;
      }
    }
  }

  .appearance-input-wrapper {
    display: flex;
    gap: 16px;

    .color-picker-input {
      ::v-deep {
        .v-input__slot {
          max-height: unset !important;

          input {
            padding-top: 16px;
            padding-bottom: 16px;
          }
        }
      }
    }
  }
</style>
